import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useDepartmentStore } from '../stores';
import { useStaffStore } from '../stores/staff.store';

import type {
  CustomerRegistrationForm,
  SupplierRegistrationFormData,
  ConstructionCompanyRegistrationForm,
  WitnessCompanyRegistrationForm,
} from '@/features/companies/types/company.types';

import { companiesService } from '@/features/companies/services/companies.service';
import { mapWitnessCompanyFormRequest } from '@/features/companies/utils/mappers/witness-company-form-mapper';
import { mapCustomerFormRequest } from '@/features/companies/utils/mappers/customer-form-mapper';
import { mapSupplierFormRequest } from '@/features/companies/utils/mappers/supplier-form-mapper';
import { mapConstructionCompanyFormRequest } from '@/features/companies/utils/mappers/construction-form-mapper';
import { paths } from '@/config/paths';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useSubmitting } from '@/hooks/use-submitting';

export const useCompanyRegistration = () => {
  const navigate = useNavigate();
  const { departments } = useDepartmentStore();
  const { staffsByDepartment } = useStaffStore();
  const { setConfirmDialog } = useConfirmDialog();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsLoading } = useLoadingProgress();
  const { setIsSubmitting } = useSubmitting();

  const handleCustomerSubmit = useCallback(
    async (data: CustomerRegistrationForm) => {
      try {
        setIsLoading(true);
        // Map form data to API request format
        const apiRequestData = mapCustomerFormRequest(data, departments);
        await companiesService.createCustomerSupplier(apiRequestData);
        setSnackbarMessage(MESSAGES.CUSTOMER_HAS_BEEN_REGISTERED);
        setIsSubmitting(true);
        navigate(paths.companies.path);
      } catch (error) {
        setConfirmDialog({
          title: MESSAGES.REGISTER_ERROR,
        });
        console.error('Error submitting customer registration:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, departments, staffsByDepartment]
  );

  const handleSupplierSubmit = useCallback(
    async (data: SupplierRegistrationFormData) => {
      try {
        setIsLoading(true);
        // Map supplier form data to API format
        const apiRequestData = mapSupplierFormRequest(data, departments);

        await companiesService.createCustomerSupplier(apiRequestData);
        setSnackbarMessage(MESSAGES.SUPPLIER_HAS_BEEN_REGISTERED);
        setIsSubmitting(true);
        navigate(paths.companies.path);
      } catch (error) {
        console.error('Error submitting supplier registration:', error);
        setConfirmDialog({
          title: MESSAGES.REGISTER_ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, departments, staffsByDepartment]
  );

  const handleConstructionSubmit = useCallback(
    async (data: ConstructionCompanyRegistrationForm) => {
      try {
        setIsLoading(true);
        // Departments and staffsByDepartment are accessed directly from store

        // Map construction form data to API format
        const apiRequestData = mapConstructionCompanyFormRequest(data, departments);

        await companiesService.createConstructionCompany(apiRequestData);
        setSnackbarMessage(MESSAGES.CONSTRUCTION_COMPANY_REGISTER_SUCCESS);
        setIsSubmitting(true);
        navigate(paths.companies.path);
      } catch (error) {
        console.error('Error submitting construction company registration:', error);
        setConfirmDialog({
          title: MESSAGES.REGISTER_ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, departments, staffsByDepartment]
  );

  const handleWitnessCompanySubmit = useCallback(
    async (data: WitnessCompanyRegistrationForm) => {
      try {
        setIsLoading(true);
        // Departments and staffsByDepartment are accessed directly from store
        // Map form data to API request format
        const apiRequestData = mapWitnessCompanyFormRequest(data, departments);

        // Call API
        await companiesService.createWitnessCompany(apiRequestData);

        // Show success message
        setSnackbarMessage(MESSAGES.WITNESS_COMPANY_HAS_BEEN_REGISTERED);

        // Navigate to companies list on success
        setIsSubmitting(true);
        navigate(paths.companies.path);
      } catch (error) {
        console.error('Error submitting witness company registration:', error);
        setConfirmDialog({
          title: MESSAGES.REGISTER_ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, departments, staffsByDepartment]
  );

  const handleCancel = useCallback(() => {
    navigate(paths.companies.path);
  }, [navigate]);

  return {
    handleCustomerSubmit,
    handleSupplierSubmit,
    handleConstructionSubmit,
    handleWitnessCompanySubmit,
    handleCancel,
  };
};
